import React, { useState } from 'react';
import { string, arrayOf, shape, func, bool } from 'prop-types';
import classNames from 'classnames';
import { Field, useField } from 'react-final-form';
import { ValidationError } from '../../components';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import { sortOptionsAlphabetically } from '../../util/sortingHelpers';

import css from './HierarchicalCategorySelect.module.css';

/**
 * HierarchicalCategorySelect - A dropdown that shows subcategories with expandable sub-subcategories
 * 
 * When a sub-subcategory is selected, both subcategory and sub-subcategory values are set automatically.
 */
const HierarchicalCategorySelect = props => {
  const {
    name,
    id,
    label,
    subCategories = [],
    subSubCategories = [],
    onChange,
    validate,
    placeholder,
    disabled,
    className,
    parentFieldName,
    formApi,
    onBeforeOpen,
  } = props;

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  
  // Use useField to subscribe to the parent field value directly
  const parentField = parentFieldName ? useField(parentFieldName, { subscription: { value: true } }) : null;
  const parentValue = parentField?.input?.value;

  // Group sub-subcategories by their parentId (which is the subcategory id)
  const groupedCategories = React.useMemo(() => {
    if (!subSubCategories || subSubCategories.length === 0) {
      return {};
    }

    const groups = {};
    subSubCategories.forEach(subSubCat => {
      const parentId = subSubCat.parentId;
      if (!groups[parentId]) {
        groups[parentId] = [];
      }
      groups[parentId].push(subSubCat);
    });

    return groups;
  }, [subSubCategories]);

  // Get subcategory label by id
  const getSubCategoryLabel = (subCategoryId) => {
    const subCategory = subCategories.find(cat =>
      cat.id == subCategoryId || String(cat.id) === String(subCategoryId)
    );
    return subCategory?.label || `Category ${subCategoryId}`;
  };

  // Toggle expand/collapse for a subcategory
  const toggleExpand = (subCategoryId) => {
    setExpanded(prev => ({
      ...prev,
      [subCategoryId]: !prev[subCategoryId]
    }));
  };

  // Handle selection of a sub-subcategory
  const handleSelect = (subCategoryId, subSubCategoryId, input) => {
    // Set both subcategory and sub-subcategory values
    if (formApi && parentFieldName) {
      formApi.batch(() => {
        formApi.change(parentFieldName, subCategoryId);
        input.onChange(subSubCategoryId);
      });
    } else {
      input.onChange(subSubCategoryId);
    }

    // Call custom onChange if provided
    if (onChange) {
      onChange(subCategoryId, subSubCategoryId);
    }

    // Close dropdown after selection
    setOpen(false);
  };

  // Get display text for selected value
  const getDisplayText = (value, parentValue) => {
    // Check if value is a sub-subcategory
    const selectedSubSubCategory = subSubCategories.find(cat => cat.id === value);

    if (selectedSubSubCategory) {
      const subCategoryLabel = getSubCategoryLabel(selectedSubSubCategory.parentId);
      return `${subCategoryLabel} | ${selectedSubSubCategory.label}`;
    }

    // If no sub-subcategory is selected but parentValue exists, show the subcategory
    // This handles the case where a subcategory without children is selected
    if (!value && parentValue) {
      const selectedSubCategory = subCategories.find(cat => 
        cat.id == parentValue || String(cat.id) === String(parentValue)
      );
      if (selectedSubCategory) {
        return selectedSubCategory.label;
      }
    }

    return '';
  };

  // Handle selection of a subcategory without children
  const handleSubCategorySelect = (subCategoryId, input) => {
    // Set subcategory value and clear sub-subcategory
    if (formApi && parentFieldName) {
      formApi.batch(() => {
        formApi.change(parentFieldName, subCategoryId);
        input.onChange(''); // Use empty string instead of null
      });
    }

    // Call custom onChange if provided
    if (onChange) {
      onChange(subCategoryId, null);
    }

    // Close dropdown after selection
    setOpen(false);
  };

  // Render the dropdown items
  const renderItems = (input) => {
    return subCategories.map(subCategory => {
      const subCategoryId = subCategory.id;
      const subSubCats = groupedCategories[subCategoryId] || [];
      const hasChildren = subSubCats.length > 0;
      const isExpanded = expanded[subCategoryId];
      const subCategoryLabel = subCategory.label;
      const hasSelectedChild = subSubCats.some(child => child.id === input.value);
      
      // Check if this subcategory is selected (when it has no children)
      // Use loose equality to handle string/number comparison
      const isSelectedWithoutChildren = !hasChildren && (parentValue == subCategoryId || String(parentValue) === String(subCategoryId));

      return (
        <div key={subCategoryId} className={css.categoryGroup}>
          {/* SubCategory Header */}
          <div
            className={classNames(css.parentItem, {
              [css.parentItemExpanded]: isExpanded,
              [css.parentItemSelected]: hasSelectedChild || isSelectedWithoutChildren,
            })}
            onClick={() => hasChildren ? toggleExpand(subCategoryId) : handleSubCategorySelect(subCategoryId, input)}
          >
            <span className={css.parentLabel}>{subCategoryLabel}</span>
          </div>

          {/* Sub-SubCategories */}
          {hasChildren && isExpanded && (
            <div className={css.childrenContainer}>
              {sortOptionsAlphabetically(subSubCats).map(subSubCat => {
                const isSelected = subSubCat.id === input.value;

                return (
                  <div
                    key={subSubCat.id}
                    className={classNames(css.childItem, {
                      [css.childItemSelected]: isSelected,
                    })}
                    onClick={() => handleSelect(subCategoryId, subSubCat.id, input)}
                  >
                    <span className={css.childLabel}>{subSubCat.label}</span>
                    {/* {isSelected && <span className={css.checkmark}>✓</span>} */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Field name={name} validate={validate}>
      {({ input, meta }) => {
        const hasError = meta.touched && meta.error;
        const displayText = getDisplayText(input.value, parentValue);

        return (
          <div className={classNames(css.root, className)}>
            {label && (
              <label htmlFor={id} className={css.label}>
                {label}
              </label>
            )}

            <div className={css.selectWrapper}>
              {/* Trigger Button */}
              <div
                className={classNames(css.trigger, {
                  [css.triggerOpen]: open,
                  [css.triggerError]: hasError,
                  [css.triggerDisabled]: disabled,
                })}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  if (!open && typeof onBeforeOpen === 'function') {
                    onBeforeOpen();
                    return;
                  }
                  setOpen(!open);
                }}
              >
                <span className={classNames(css.triggerText, {
                  [css.triggerTextPlaceholder]: !displayText
                })}>
                  {displayText || placeholder || 'Select...'}
                </span>
                <span className={css.triggerArrow}><svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.18579 4.81455C4.36 4.98787 4.64 4.98787 4.81421 4.81455L8.36967 1.25909C8.54344 1.08487 8.54344 0.803992 8.36967 0.630663C8.1959 0.456446 7.91457 0.456446 7.74124 0.630663L4.5 3.87146L1.25876 0.630663C1.08498 0.456446 0.803658 0.456446 0.63033 0.630663C0.456557 0.803992 0.456557 1.08487 0.63033 1.25909L4.18579 4.81455Z" fill="#4A4A4A" stroke="#4A4A4A" />
                </svg>
                </span>
              </div>

              {/* Dropdown */}
              {open && !disabled && (
                <OutsideClickHandler
                  className={css.dropdown}
                  onOutsideClick={() => setOpen(false)}
                >
                  <div className={css.dropdownContent}>
                    {subCategories.length > 0 ? (
                      renderItems(input)
                    ) : (
                      <div className={css.noCategories}>
                        No categories available
                      </div>
                    )}
                  </div>
                </OutsideClickHandler>
              )}
            </div>

            {hasError && (
              <ValidationError className={css.error}>
                {meta.error}
              </ValidationError>
            )}
          </div>
        );
      }}
    </Field>
  );
};

HierarchicalCategorySelect.defaultProps = {
  placeholder: 'Select a category',
  disabled: false,
  className: null,
  onBeforeOpen: null,
  validate: null,
  onChange: null,
  parentFieldName: null,
  formApi: null,
};

HierarchicalCategorySelect.propTypes = {
  name: string.isRequired,
  id: string.isRequired,
  label: string,
  subCategories: arrayOf(
    shape({
      id: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  subSubCategories: arrayOf(
    shape({
      id: string.isRequired,
      label: string.isRequired,
      parentId: string.isRequired,
    })
  ).isRequired,
  onChange: func,
  validate: func,
  placeholder: string,
  disabled: bool,
  className: string,
  parentFieldName: string,
  formApi: shape({
    change: func.isRequired,
    batch: func.isRequired,
  }),
  onBeforeOpen: func,
};

export default HierarchicalCategorySelect;
